import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid
} from '@mui/material';
import { Add, Edit, Delete } from '@mui/icons-material';
import { useDataContext } from '../contexts/DataContext';
import { formatDateForDateTimeLocal, formatDateTimeLocalToISO } from '../utils/dateUtils';

const ConsultationsList = ({ consultations, patientId, onUpdate }) => {
  const { addConsultation, updateConsultation, deleteConsultation } = useDataContext();
  const [openDialog, setOpenDialog] = useState(false);
  const [editingConsultation, setEditingConsultation] = useState(null);
  const [formData, setFormData] = useState({
    consultation_date: '',
    reason: '',
    diagnosis: '',
    additional_exams: '',
    recommendations: '',
    orientation: '',
    next_appointment: '',
    notes: ''
  });

  const handleOpenDialog = (consultation = null) => {
    if (consultation) {
      setEditingConsultation(consultation);
      setFormData({
        consultation_date: formatDateForDateTimeLocal(consultation.consultation_date),
        reason: consultation.reason || '',
        diagnosis: consultation.diagnosis || '',
        additional_exams: consultation.additional_exams || '',
        recommendations: consultation.recommendations || '',
        orientation: consultation.orientation || '',
        next_appointment: formatDateForDateTimeLocal(consultation.next_appointment),
        notes: consultation.notes || ''
      });
    } else {
      setEditingConsultation(null);
      setFormData({
        consultation_date: formatDateForDateTimeLocal(new Date().toISOString()),
        reason: '',
        diagnosis: '',
        additional_exams: '',
        recommendations: '',
        orientation: '',
        next_appointment: '',
        notes: ''
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingConsultation(null);
    setFormData({
      consultation_date: '',
      reason: '',
      diagnosis: '',
      additional_exams: '',
      recommendations: '',
      orientation: '',
      next_appointment: '',
      notes: ''
    });
  };

  const handleSubmit = async () => {
    try {
      const consultationData = {
        patientId: patientId,
        consultation_date: formatDateTimeLocalToISO(formData.consultation_date),
        reason: formData.reason || undefined,
        diagnosis: formData.diagnosis || undefined,
        additional_exams: formData.additional_exams || undefined,
        recommendations: formData.recommendations || undefined,
        orientation: formData.orientation || undefined,
        next_appointment: formData.next_appointment ? formatDateTimeLocalToISO(formData.next_appointment) : undefined,
        notes: formData.notes || undefined
      };

      if (editingConsultation) {
        await updateConsultation(editingConsultation.id, consultationData);
      } else {
        await addConsultation(consultationData);
      }

      handleCloseDialog();
      if (onUpdate) {
        await onUpdate();
      }
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
      
      // Afficher les erreurs de validation si elles existent
      if (error.response?.data?.errors) {
        const validationErrors = error.response.data.errors;
        const errorMessages = validationErrors.map(err => err.msg).join('\n');
        alert(`Erreurs de validation:\n${errorMessages}`);
      } else {
        alert('Erreur lors de la sauvegarde. Veuillez vérifier vos données.');
      }
    }
  };

  const handleDelete = async (consultationId) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cette consultation ?')) {
      try {
        await deleteConsultation(consultationId);
        if (onUpdate) onUpdate();
      } catch (error) {
        console.error('Erreur lors de la suppression:', error);
      }
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6">Consultations</Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => handleOpenDialog()}
        >
          Nouvelle Consultation
        </Button>
      </Box>

      {consultations && consultations.length > 0 ? (
        consultations
          .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
          .map((consultation) => (
          <Card key={consultation.id} sx={{ mb: 2 }}>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="start" mb={2}>
                <Box>
                  <Typography variant="h6" gutterBottom>
                    {formatDate(consultation.consultation_date)}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Médecin: {consultation.first_name} {consultation.last_name}
                  </Typography>
                </Box>
                <Box>
                  <Button
                    size="small"
                    startIcon={<Edit />}
                    onClick={() => handleOpenDialog(consultation)}
                  >
                    Modifier
                  </Button>
                  <Button
                    size="small"
                    color="error"
                    startIcon={<Delete />}
                    onClick={() => handleDelete(consultation.id)}
                  >
                    Supprimer
                  </Button>
                </Box>
              </Box>

              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" gutterBottom>
                    <strong>Motif:</strong>
                  </Typography>
                  <Typography variant="body2" paragraph>
                    {consultation.reason}
                  </Typography>
                </Grid>

                {consultation.diagnosis && (
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" gutterBottom>
                      <strong>Diagnostic:</strong>
                    </Typography>
                    <Typography variant="body2" paragraph>
                      {consultation.diagnosis}
                    </Typography>
                  </Grid>
                )}

                {consultation.recommendations && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" gutterBottom>
                      <strong>Recommandations:</strong>
                    </Typography>
                    <Typography variant="body2" paragraph>
                      {consultation.recommendations}
                    </Typography>
                  </Grid>
                )}

                {consultation.next_appointment && (
                  <Grid item xs={12}>
                    <Chip
                      label={`Prochain RDV: ${formatDate(consultation.next_appointment)}`}
                      color="primary"
                      variant="outlined"
                    />
                  </Grid>
                )}
              </Grid>
            </CardContent>
          </Card>
        ))
      ) : (
        <Typography variant="body1" color="textSecondary">
          Aucune consultation enregistrée
        </Typography>
      )}

      {/* Dialog pour ajouter/modifier une consultation */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingConsultation ? 'Modifier la consultation' : 'Nouvelle consultation'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Date de consultation"
                type="datetime-local"
                value={formData.consultation_date}
                onChange={(e) => setFormData({ ...formData, consultation_date: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Prochain rendez-vous"
                type="datetime-local"
                value={formData.next_appointment}
                onChange={(e) => setFormData({ ...formData, next_appointment: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Motif de consultation"
                multiline
                rows={2}
                value={formData.reason}
                onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Diagnostic"
                multiline
                rows={2}
                value={formData.diagnosis}
                onChange={(e) => setFormData({ ...formData, diagnosis: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Examens complémentaires"
                multiline
                rows={2}
                value={formData.additional_exams}
                onChange={(e) => setFormData({ ...formData, additional_exams: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Recommandations"
                multiline
                rows={2}
                value={formData.recommendations}
                onChange={(e) => setFormData({ ...formData, recommendations: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Orientation"
                value={formData.orientation}
                onChange={(e) => setFormData({ ...formData, orientation: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Notes"
                multiline
                rows={3}
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Annuler</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editingConsultation ? 'Modifier' : 'Créer'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ConsultationsList;
